import { useEffect, useMemo, useState } from "react";
import { RefreshCw, CreditCard, AlertTriangle, ExternalLink } from "lucide-react";
import toast from "react-hot-toast";
import {
    createBillingCheckout,
    createBillingPortal,
    getCurrentBillingUsage
} from "../../services/crmService";
import "./BillingUsageCard.css";

function getLimit(usage, key) {
    const value = usage?.limits?.[key];

    if (value === undefined || value === null) return null;

    const number = Number(value);
    return Number.isFinite(number) ? number : null;
}

function formatLimit(limit) {
    if (limit === null || limit === undefined) return "Unlimited";
    if (limit >= 999999) return "Unlimited";
    return String(limit);
}

function percent(used, limit) {
    if (!limit || limit >= 999999) return 0;
    return Math.min(100, Math.round((Number(used || 0) / limit) * 100));
}

function UsageRow({ label, used, limit }) {
    const pct = percent(used, limit);
    const isWarning = limit !== null && limit < 999999 && pct >= 80;
    const isFull = limit !== null && limit < 999999 && Number(used || 0) >= limit;

    return (
        <div className={`billing-usage-row ${isFull ? "full" : isWarning ? "warning" : ""}`}>
            <div className="billing-usage-row-top">
                <span>{label}</span>
                <strong>
                    {used || 0} / {formatLimit(limit)}
                </strong>
            </div>

            <div className="billing-usage-bar">
                <div style={{ width: `${pct}%` }} />
            </div>
        </div>
    );
}

function normalizePlanLabel(value = "") {
    return String(value || "trial")
        .replace(/[_-]/g, " ")
        .replace(/\b\w/g, (x) => x.toUpperCase());
}

function normalizeError(error, fallback) {
    return (
        error?.response?.data?.message ||
        error?.response?.data?.title ||
        error?.response?.data?.Message ||
        error?.message ||
        fallback
    );
}

function getUpgradeOptions(usage) {
    const accountType = String(usage?.accountType || "").toLowerCase();

    if (accountType === "agency") {
        return [
            {
                planKey: "agency_starter",
                label: "Agency Starter"
            },
            {
                planKey: "agency_growth",
                label: "Agency Growth"
            },
            {
                planKey: "agency_pro",
                label: "Agency Pro"
            }
        ];
    }

    return [
        {
            planKey: "starter",
            label: "Starter"
        },
        {
            planKey: "growth",
            label: "Growth"
        },
        {
            planKey: "pro",
            label: "Pro"
        }
    ];
}

export default function BillingUsageCard({ compact = false }) {
    const [usage, setUsage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [checkoutBusyPlan, setCheckoutBusyPlan] = useState("");
    const [portalBusy, setPortalBusy] = useState(false);

    const billingProvider =
        process.env.REACT_APP_BILLING_PROVIDER || "razorpay";

    const canUseBillingPortal =
        billingProvider.toLowerCase() === "stripe";

    const load = async () => {
        try {
            setLoading(true);
            const data = await getCurrentBillingUsage();
            setUsage(data || null);
        } catch (error) {
            console.error(error);
            toast.error(
                normalizeError(error, "Could not load billing usage.")
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const rows = useMemo(() => {
        if (!usage) return [];

        const baseRows = [
            {
                label: "Projects",
                used: usage.projectsUsed,
                limit: getLimit(usage, "projects.max")
            },
            {
                label: "Custom Domains",
                used: usage.customDomainsUsed,
                limit: getLimit(usage, "custom_domains.max")
            },
            {
                label: "AI Generations",
                used: usage.aiGenerationsThisMonth,
                limit: getLimit(usage, "ai_generations.monthly")
            },
            {
                label: "Leads This Month",
                used: usage.leadsThisMonth,
                limit: getLimit(usage, "leads.monthly")
            }
        ];

        if (String(usage.accountType || "").toLowerCase() === "agency") {
            return [
                {
                    label: "Agency Clients",
                    used: usage.agencyClientsUsed,
                    limit: getLimit(usage, "agency_clients.max")
                },
                {
                    label: "Agency Projects",
                    used: usage.agencyProjectsUsed,
                    limit: getLimit(usage, "agency_projects.max")
                },
                {
                    label: "Agency Domains",
                    used: usage.agencyCustomDomainsUsed,
                    limit: getLimit(usage, "agency_custom_domains.max")
                },
                {
                    label: "Agency AI Usage",
                    used: usage.agencyAiGenerationsThisMonth,
                    limit: getLimit(usage, "agency_ai_generations.monthly")
                }
            ];
        }

        return baseRows;
    }, [usage]);

    const upgradeOptions = useMemo(() => {
        return getUpgradeOptions(usage);
    }, [usage]);

    const billingManagedByAgency =
        usage?.isBillingManagedByParent ||
        (
            String(usage?.accountType || "").toLowerCase() === "client" &&
            usage?.billingOwnerAccountId &&
            String(usage.billingOwnerAccountId) !== String(usage.accountId)
        );

    const canSelfManageBilling = !billingManagedByAgency;

    const openRazorpayCheckout = (result) => {
        if (!window.Razorpay) {
            toast.error("Razorpay checkout script is not loaded.");
            return;
        }

        const razorpay = new window.Razorpay({
            key: result.keyId,
            subscription_id: result.subscriptionId,
            name: "Shriansh Nexus",
            description: `Subscription: ${normalizePlanLabel(result.planKey)}`,
            handler: function () {
                toast.success("Payment completed. Billing will update shortly.");
                setTimeout(() => {
                    load();
                }, 1800);
            },
            modal: {
                ondismiss: function () {
                    toast("Checkout closed.");
                }
            },
            theme: {
                color: "#2563eb"
            }
        });

        razorpay.open();
    };

    const startCheckout = async (planKey) => {
        try {
            setCheckoutBusyPlan(planKey);

            const result = await createBillingCheckout(planKey);

            if (!result?.success) {
                toast.error(result?.message || "Could not start checkout.");
                return;
            }

            if (result.provider === "razorpay") {
                if (!result.subscriptionId || !result.keyId) {
                    toast.error("Razorpay subscription details were not returned.");
                    return;
                }

                openRazorpayCheckout(result);
                return;
            }

            if (result.provider === "stripe" || result.url) {
                if (!result?.url) {
                    toast.error("Checkout URL was not returned.");
                    return;
                }

                window.location.href = result.url;
                return;
            }

            toast.error("Unsupported billing provider response.");
        } catch (error) {
            console.error(error);
            toast.error(normalizeError(error, "Could not start checkout."));
        } finally {
            setCheckoutBusyPlan("");
        }
    };

    const openBillingPortal = async () => {
        try {
            setPortalBusy(true);

            const result = await createBillingPortal();

            if (!result?.success) {
                toast.error(result?.message || "Billing portal is not available.");
                return;
            }

            if (!result?.url) {
                toast.error(result?.message || "Billing portal URL was not returned.");
                return;
            }

            window.location.href = result.url;
        } catch (error) {
            console.error(error);
            toast.error(normalizeError(error, "Could not open billing portal."));
        } finally {
            setPortalBusy(false);
        }
    };

    if (loading) {
        return (
            <section className={`billing-usage-card ${compact ? "compact" : ""}`}>
                <div className="billing-usage-head">
                    <div className="billing-usage-icon">
                        <CreditCard size={18} />
                    </div>
                    <div>
                        <span>Billing</span>
                        <h3>Loading usage...</h3>
                    </div>
                </div>
            </section>
        );
    }

    if (!usage) {
        return (
            <section className={`billing-usage-card ${compact ? "compact" : ""}`}>
                <div className="billing-usage-empty">
                    <AlertTriangle size={20} />
                    <strong>Billing usage unavailable</strong>
                    <button type="button" onClick={load}>
                        Retry
                    </button>
                </div>
            </section>
        );
    }

    return (
        <section className={`billing-usage-card ${compact ? "compact" : ""}`}>
            <div className="billing-usage-head">
                <div className="billing-usage-icon">
                    <CreditCard size={18} />
                </div>

                <div>
                    <span>Billing & Usage</span>
                    <h3>{normalizePlanLabel(usage.billingPlanKey)}</h3>
                    <p>
                        {normalizePlanLabel(usage.accountType)} ·{" "}
                        {normalizePlanLabel(usage.subscriptionStatus)}
                    </p>
                </div>

                <button
                    type="button"
                    className="billing-refresh-btn"
                    onClick={load}
                    title="Refresh billing usage"
                >
                    <RefreshCw size={16} />
                </button>
            </div>

            <div className="billing-usage-list">
                {rows.map((row) => (
                    <UsageRow
                        key={row.label}
                        label={row.label}
                        used={row.used}
                        limit={row.limit}
                    />
                ))}
            </div>

            {canSelfManageBilling ? (
                <div className="billing-actions-panel">
                    <div>
                        <span>Upgrade Plan</span>
                        <p>
                            Upgrade your plan using Razorpay test checkout. Stripe can be enabled later.
                        </p>
                    </div>

                    <div className="billing-plan-actions">
                        {upgradeOptions.map((option) => (
                            <button
                                key={option.planKey}
                                type="button"
                                className="billing-plan-btn"
                                disabled={!!checkoutBusyPlan || portalBusy}
                                onClick={() => startCheckout(option.planKey)}
                            >
                                {checkoutBusyPlan === option.planKey
                                    ? "Opening..."
                                    : option.label}
                            </button>
                        ))}

                        {canUseBillingPortal ? (
                            <button
                                type="button"
                                className="billing-plan-btn secondary"
                                disabled={!!checkoutBusyPlan || portalBusy}
                                onClick={openBillingPortal}
                            >
                                <ExternalLink size={14} />
                                {portalBusy ? "Opening..." : "Manage Billing"}
                            </button>
                        ) : (
                            <button
                                type="button"
                                className="billing-plan-btn secondary"
                                disabled
                                title="Razorpay portal is not available yet"
                            >
                                <ExternalLink size={14} />
                                Managed by Razorpay
                            </button>
                        )}
                    </div>
                </div>
            ) : (
                <div className="billing-managed-notice">
                    <strong>Billing managed by agency</strong>
                    <p>
                        Your package and billing are managed by{" "}
                        {usage.billingOwnerAccountName || "your agency"}.
                        Contact them to upgrade your plan.
                    </p>
                </div>
            )}
        </section>
    );
}